/**
 * RTWO Cryptography & Identity Engine — Base Key Provider
 */

import type {
  KeyProviderStatus,
  KeyProviderType,
  RTWOKeyProviderDescriptor,
} from '../adapters/key-provider-interface';

export abstract class BaseKeyProvider {
  abstract readonly providerId: string;
  abstract readonly providerName: string;
  abstract readonly providerType: KeyProviderType;

  protected status: KeyProviderStatus = 'not_configured';

  async connect(): Promise<void> {
    this.status = 'connecting';
    try {
      await this.onConnect();
      this.status = 'connected';
    } catch {
      this.status = 'error';
      throw new Error(`${this.providerName} failed to connect.`);
    }
  }

  async disconnect(): Promise<void> {
    await this.onDisconnect();
    this.status = 'disconnected';
  }

  getStatus(): KeyProviderStatus {
    return this.status;
  }

  isReady(): boolean {
    return this.status === 'connected';
  }

  getDescriptor(): RTWOKeyProviderDescriptor {
    return {
      providerId: this.providerId,
      providerName: this.providerName,
      providerType: this.providerType,
      status: this.status,
      isConfigured: this.providerType !== 'not_configured',
      configuredAt: this.status === 'connected' ? Date.now() : null,
      description: `${this.providerName} key provider.`,
    };
  }

  protected abstract onConnect(): Promise<void>;
  protected abstract onDisconnect(): Promise<void>;
}
